import { useEffect, useState } from 'react';
import { Maximize2, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api, apiError } from '@/lib/api';
import { PriceButton } from './PriceControls';

export const useFlyerPreview = (draft, enabled, source) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!enabled || !draft) return;
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post('/admin/price-list/preview', draft, { signal: controller.signal });
        if (!controller.signal.aborted) { setPreview(data); setError(''); }
      } catch (e) { if (!controller.signal.aborted) setError(apiError(e)); }
      finally { if (!controller.signal.aborted) setLoading(false); }
    }, 550);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [draft, enabled, source]);
  return { preview, loading, error };
};

const PreviewBody = ({ state, prefix }) => <>
  <div className="pl-preview-status" data-testid={`${prefix}-status`} aria-live="polite">
    {state.loading ? <><Loader2 size={16} className="animate-spin" /> Vorschau wird aktualisiert …</> : state.error ? <><AlertTriangle size={16} /> Vorschau nicht aktuell</> : state.preview?.fits ? <><CheckCircle2 size={16} /> Druckfähig · {state.preview.selected_count} Einträge</> : <><AlertTriangle size={16} /> Layout prüfen</>}
  </div>
  {state.error && <p role="alert" className="pl-warning" data-testid={`${prefix}-error`}>{state.error}</p>}
  {!!state.preview?.warnings.length && <div className="pl-warning" role="alert" data-testid={`${prefix}-warnings`}>{state.preview.warnings.map((text, i) => <p key={i}>{text}</p>)}</div>}
  <div className="pl-paper" data-testid={`${prefix}-paper`} aria-busy={state.loading}>
    {state.preview ? <img data-testid={`${prefix}-image`} src={state.preview.image} alt="Preislisten-Faltflyer, Innenseite: zwei gleich große A5-Hälften auf A4 quer" /> : <div className="pl-paper-loading"><Loader2 size={25} className="animate-spin" /></div>}
  </div>
  <p className="pl-paper-caption" data-testid={`${prefix}-caption-front`}>Seite 1 · Innenseite (Preisliste)</p>
  {state.preview?.back_image && <>
    <div className="pl-paper" data-testid={`${prefix}-paper-back`} aria-busy={state.loading}>
      <img data-testid={`${prefix}-back-image`} src={state.preview.back_image} alt="Preislisten-Faltflyer, Außenseite mit Trainingsclub-Werbung oder klassischem Titelblatt" />
    </div>
    <p className="pl-paper-caption" data-testid={`${prefix}-caption-back`}>Seite 2 · Außenseite · um 180° gedreht für Duplexdruck · mittig falzen</p>
  </>}
</>;

export const FlyerPreview = ({ state, onExpand }) => <aside className="pl-preview" data-testid="pl-live-preview">
  <div className="pl-preview-title"><div><h2>Live-Vorschau</h2><span>A4 QUER · 297 × 210 MM · 2 × A5</span></div>
    <PriceButton id="pl-expand-preview" title="Druckvorschau vergrößern" aria-label="Druckvorschau vergrößern" onClick={onExpand}><Maximize2 size={17} /></PriceButton>
  </div><PreviewBody state={state} prefix="pl-live" />
</aside>;

export const FlyerPreviewDialog = ({ open, onClose, state }) => <Dialog open={open} onOpenChange={value => !value && onClose()}>
  <DialogContent className="pl pl-preview-dialog" data-testid="pl-preview-dialog"><DialogHeader>
    <DialogTitle data-testid="pl-print-preview-title">Druckvorschau</DialogTitle><DialogDescription>A4 quer · mittig faltbar · identisches PDF-Layout</DialogDescription>
  </DialogHeader><PreviewBody state={state} prefix="pl-modal" /></DialogContent>
</Dialog>;